import { Directive, ElementRef, HostListener } from '@angular/core';

/** How far the image is magnified while the pointer is over it. */
const ZOOM_SCALE = 2;

/**
 * Magnifies the image inside the host element while the pointer moves across it, panning so
 * that whatever is under the cursor sits in the middle of the frame, and puts it back when the
 * pointer leaves.
 *
 * Extracted from the zoom the retired legacy onboarding requests page applied inline on its own
 * image viewer, so any viewer can reuse it without its own copy of the arithmetic. The host
 * should clip its overflow — the scaled image is deliberately larger than its frame.
 */
@Directive({
    selector: '[appHoverZoom]',
    standalone: false
})
export class HoverZoomDirective {
  constructor(private host: ElementRef<HTMLElement>) { }

  /**
   * Scales the image and pans it towards the cursor.
   * @param event the pointer move over the host
   */
  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    const image = this.image;
    if (!image) return;

    const bounds = this.host.nativeElement.getBoundingClientRect();
    // Offset of the cursor from the middle of the frame, magnified by the same factor as the
    // image — which is what brings the point under the cursor into the middle.
    const translateX = (bounds.width / 2 - (event.clientX - bounds.left)) * ZOOM_SCALE;
    const translateY = (bounds.height / 2 - (event.clientY - bounds.top)) * ZOOM_SCALE;

    image.style.transform = `translate(${translateX}px, ${translateY}px) scale(${ZOOM_SCALE})`;
  }

  /** Drops the zoom on the way out, so the frame is never left mid-pan. */
  @HostListener('mouseleave')
  onMouseLeave(): void {
    const image = this.image;
    if (image) {
      image.style.transform = 'translate(0%, 0%) scale(1)';
    }
  }

  /**
   * The image being magnified — the first one inside the host.
   * @returns the image element, or null before it has rendered
   */
  private get image(): HTMLElement | null {
    return this.host.nativeElement.querySelector('img');
  }
}
